/** Blood pressure in cmHg. A missing side was not filled in. */
export type BloodPressure = {
  systolic?: number;
  diastolic?: number;
};

type Range = { min: number; max: number };

/**
 * Valid cmHg values. Each max stays below 10 × its min, so a number never fits
 * both as cmHg and as mmHg without its trailing 0.
 */
const SYSTOLIC_RANGE: Range = { min: 5, max: 30 };
const DIASTOLIC_RANGE: Range = { min: 2, max: 15 };

// Two numbers of up to 3 digits, either one may be empty, split by '/', ':', '-' or spaces
const BLOOD_PRESSURE_PATTERN = /^(\d{0,3})(?:\s*[/:-]\s*|\s+)(\d{0,3})$/;

const isInRange = (value: number, { min, max }: Range) =>
  value >= min && value <= max;

/** Reads a number as cmHg. When it only fits the range without its trailing 0, it is mmHg (80 → 8). */
const toCmHg = (digits: string, range: Range) => {
  const value = Number(digits);
  return !isInRange(value, range) && value % 10 === 0 ? value / 10 : value;
};

const isValid = ({ systolic, diastolic }: BloodPressure) =>
  (systolic === undefined || isInRange(systolic, SYSTOLIC_RANGE)) &&
  (diastolic === undefined || isInRange(diastolic, DIASTOLIC_RANGE)) &&
  (systolic === undefined ||
    diastolic === undefined ||
    systolic - diastolic > 1);

/**
 * Builds a blood pressure from the digits of each side ('' or undefined for an empty side).
 * Returns `undefined` when a side is out of range or the systolic is not above the diastolic.
 */
export const toBloodPressure = (
  systolicDigits?: string,
  diastolicDigits?: string
): BloodPressure | undefined => {
  const bloodPressure: BloodPressure = {
    systolic: systolicDigits
      ? toCmHg(systolicDigits, SYSTOLIC_RANGE)
      : undefined,
    diastolic: diastolicDigits
      ? toCmHg(diastolicDigits, DIASTOLIC_RANGE)
      : undefined
  };
  return isValid(bloodPressure) ? bloodPressure : undefined;
};

/**
 * Reads the stored blood pressure text, e.g. "12/8", "120-80", "13/" or "/8".
 * Returns `{}` for an empty value and `undefined` for free text that is not a blood pressure.
 */
export const parseBloodPressure = (
  text?: string | null
): BloodPressure | undefined => {
  const trimmed = text?.trim();
  if (!trimmed) {
    return {};
  }
  const match = BLOOD_PRESSURE_PATTERN.exec(trimmed);
  return match ? toBloodPressure(match[1], match[2]) : undefined;
};

/**
 * Writes the stored pressure ("12/8", "12/", "/8"). Empty text clears the value;
 * the PATCH endpoint ignores null.
 */
export const formatBloodPressure = ({
  systolic,
  diastolic
}: BloodPressure): string =>
  systolic === undefined && diastolic === undefined
    ? ''
    : `${systolic ?? ''}/${diastolic ?? ''}`;
